import { useState, useEffect } from 'react';

interface AnimatedScoreMeterProps {
  score: number;
  maxScore?: number;
  size?: 'small' | 'medium' | 'large';
  showAnimation?: boolean;
}

export default function AnimatedScoreMeter({ 
  score, 
  maxScore = 1000, 
  size = 'large',
  showAnimation = true 
}: AnimatedScoreMeterProps) {
  const [displayScore, setDisplayScore] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const sizeConfig = {
    small: { width: 160, height: 160, radius: 70, strokeWidth: 12, fontSize: 'text-3xl' },
    medium: { width: 200, height: 200, radius: 90, strokeWidth: 14, fontSize: 'text-4xl' },
    large: { width: 256, height: 256, radius: 120, strokeWidth: 16, fontSize: 'text-6xl' }
  };

  const config = sizeConfig[size];
  const circumference = 2 * Math.PI * config.radius;
  const progress = (displayScore / maxScore) * circumference;

  useEffect(() => {
    if (!showAnimation) {
      setDisplayScore(score);
      return;
    }

    setIsAnimating(true);
    const duration = 2000;
    const steps = 60;
    const increment = score / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= score) {
        setDisplayScore(score);
        setIsAnimating(false);
        clearInterval(timer);
      } else {
        setDisplayScore(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [score, showAnimation]);

  const getScoreColor = () => {
    const percentage = (displayScore / maxScore) * 100;
    if (percentage >= 90) return '#10b981'; // green
    if (percentage >= 80) return '#14b8a6'; // teal
    if (percentage >= 70) return '#3b82f6'; // blue
    if (percentage >= 60) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  const getScoreLabel = () => {
    const percentage = (displayScore / maxScore) * 100;
    if (percentage >= 90) return 'Excellent';
    if (percentage >= 80) return 'Very Good';
    if (percentage >= 70) return 'Good';
    if (percentage >= 60) return 'Fair';
    return 'Needs Attention';
  };

  return (
    <div className="relative inline-block">
      <svg 
        width={config.width} 
        height={config.height}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={config.width / 2}
          cy={config.height / 2}
          r={config.radius}
          stroke="#e5e7eb"
          strokeWidth={config.strokeWidth}
          fill="none"
        />
        {/* Animated progress circle */}
        <circle
          cx={config.width / 2}
          cy={config.height / 2}
          r={config.radius}
          stroke={getScoreColor()}
          strokeWidth={config.strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
          style={{
            filter: 'drop-shadow(0 0 8px rgba(20, 184, 166, 0.3))'
          }}
        />
      </svg>

      {/* Center content */}
      <div 
        className="absolute inset-0 flex flex-col items-center justify-center"
        style={{ width: config.width, height: config.height }}
      >
        <div 
          className={`${config.fontSize} font-bold text-gray-900 transition-all duration-300 ${
            isAnimating ? 'scale-110' : 'scale-100'
          }`}
        >
          {displayScore}
        </div>
        <div className="text-sm text-gray-600 mt-1">out of {maxScore}</div>
        <div 
          className="mt-2 px-3 py-1 rounded-full text-xs font-semibold"
          style={{ 
            backgroundColor: `${getScoreColor()}20`,
            color: getScoreColor()
          }}
        >
          {getScoreLabel()}
        </div>
      </div>

      {/* Pulse animation ring */}
      {isAnimating && (
        <div 
          className="absolute inset-0 rounded-full animate-ping opacity-20"
          style={{ 
            backgroundColor: getScoreColor(),
            width: config.width,
            height: config.height
          }}
        />
      )}
    </div>
  );
}
